import React, { useState, useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import './FeatureTabTray.css';

function resolveTabSrc(tab, isMobile) {
  return isMobile && tab.mobileSrc ? tab.mobileSrc : tab.src;
}

const preloadCache = new Map();

function preloadImage(src) {
  if (!src) return Promise.resolve();
  if (preloadCache.has(src)) return preloadCache.get(src);
  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = src;
  });
  preloadCache.set(src, promise);
  return promise;
}

/**
 * Pill tab tray + image viewport. All tab images stay mounted (opacity swap) so
 * switches never flash blank; assets are preloaded on mount.
 */
export default function FeatureTabTray({ tabs = [], onImageClick, className = '' }) {
  const isMobile = useIsMobile(767, 900);
  const [activeId, setActiveId] = useState(tabs[0]?.id ?? null);

  const active = tabs.find((t) => t.id === activeId) || tabs[0];

  useEffect(() => {
    tabs.forEach((tab) => {
      preloadImage(resolveTabSrc(tab, isMobile));
      preloadImage(tab.src);
      if (tab.mobileSrc) preloadImage(tab.mobileSrc);
    });
  }, [tabs, isMobile]);

  if (!tabs.length || !active) return null;

  return (
    <div className={`feature-tab-tray${className ? ` ${className}` : ''}`}>
      <div className="feature-tab-tray__viewport" aria-live="polite">
        {tabs.map((tab) => {
          const src = resolveTabSrc(tab, isMobile);
          const isTabActive = tab.id === active.id;
          return (
            <img
              key={tab.id}
              src={src}
              alt={tab.alt || ''}
              className={`feature-tab-tray__img${
                isTabActive ? ' feature-tab-tray__img--active' : ''
              }`}
              aria-hidden={!isTabActive}
              tabIndex={isTabActive ? 0 : -1}
              onClick={
                isTabActive
                  ? () => onImageClick?.(src, tab.viewerAlt || tab.alt || '')
                  : undefined
              }
            />
          );
        })}
      </div>
      <div className="feature-tab-tray__tabs" role="tablist" aria-label="Platform features">
        {tabs.map((tab) => {
          const isTabActive = tab.id === active.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isTabActive}
              className={`feature-tab-tray__tab${isTabActive ? ' feature-tab-tray__tab--active' : ''}`}
              onClick={() => setActiveId(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
